import { maskPhoneNumbers } from "./pii-scrubber.js";
import { hasAmbiguousHalfAmount, normalizeViText, parseVndAmountMinor } from "./vi-normalizer.js";
import { COMPOUND_HEAD_BEFORE_NUMBER_RE, rewriteViNumberWords } from "./vi-number-words.js";
import {
  CLASSIFIER_NOUNS,
  HEAD_COUNT_NOUNS,
  KINSHIP_PERSON_NOUNS,
  LABEL_NOUNS_BEFORE_NUMBER,
  PRONOUN_LIKE_PERSON_NOUNS,
  countNounAlternation,
  countNounForms,
  type CountNoun,
} from "./vi-count-nouns.js";

/**
 * money-parser (US-AIV2-0002 · @UC0209) — BỘ ĐỔI TIỀN của trợ lý, tách khỏi `form-prefill.ts` (v1, đã xoá).
 *
 * Đọc TIỀN trong một câu tiếng Việt và **không đoán** khi mơ hồ. Hành vi chép từ `extractAmount` v1
 * (form-prefill: US-AI-0297-S01 · US-AI-0473-S03 · US-AI-0772/0773 · REVIEW HIGH-1) — v1 còn ĐƯỜNG ĐỌC THỨ HAI
 * chặt hơn ở đường chat (`intent-classifier.extractAmountMinor`, khoá `@SEC-WRONG-02`), bản này GỘP luật chặt
 * hơn đó vào (review PR #796 MEDIUM-2) vì đây là viên gạch DUY NHẤT còn lại cho `money_span`:
 *   1. Bỏ `TraceCollector` (thuộc `orchestration/` v1 đã xoá) — lý do trả về bằng GIÁ TRỊ.
 *   2. Ca nhiều ứng viên trả **TẬP ứng viên** thay vì chỉ `null`, để resolver `money_span` (0022)
 *      hỏi người dùng đích danh *"500.000 đ hay 1.000.000 đ?"* — không chọn hộ (ADR-0009 · Người làm).
 *   3. Số TRẦN (không đơn vị tiền) chỉ là tiền khi có **≥4 chữ số** ("200.000", "150000"); số trần nhỏ
 *      ("2 bạn đóng rồi" ⇒ 2) và số của KỲ ("tháng 7", "T7") KHÔNG phải tiền — đường form-prefill v1 từng đọc
 *      "Xoá đợt thu tháng 7" thành 7 đồng.
 *   4. Số trần đứng ngay sau số-có-đơn-vị ("1 triệu 2", "1tr2") là ĐUÔI SỐ GHÉP ⇒ mơ hồ bậc ⇒ hỏi (v1 form-prefill
 *      trả 2 ứng viên [1.000.000, 2] — cũng là hỏi; luật 3 mà thiếu vế này sẽ chọn 1.000.000 tự tin).
 *   5. (US-AIV2-0022, kế thừa review #796 LOW-6) Số trần mang HÌNH NGÀY/GIỜ đứng sau số-có-đơn-vị ("thu 200k 15/9",
 *      "200k hạn 30-9") KHÔNG phải đuôi số ghép — xét TRƯỚC luật 4. Không có vế này, "thu 200k 15/9" bị hỏi thừa
 *      (chiều an toàn nhưng phiền); có vế này mà viết rộng quá ("1 triệu 2" cũng lọt) là chiều SAI — test đột biến
 *      ngược chiều giữ đúng ranh: chỉ `\d{1,2}/\d{1,2}` · `\d{1,2}-\d{1,2}` (ngày/tháng) mới được bỏ qua.
 *   6. 🔴 (AI-INTERP N1) đọc MỌI đề cập số, không chỉ số mang đơn vị: số trần nhỏ mà không GIẢI THÍCH được vai (kỳ · ngày · năm · giờ · loại từ đếm
 *      ngay sau · nhãn ngay trước — `isExplainedSmallNumber`) và số dính tiếng lóng ("2m" · "3lít") là đề cập CHƯA RÕ ⇒ câu có số tiền mà còn đề cập
 *      đó thì HỎI (`AMBIGUOUS_MENTIONS`); hai đề cập TIỀN cùng giá trị mà giữa chúng có chữ nội dung là HAI khoản ⇒ HỎI (không gộp). Không có số
 *      tiền nào ⇒ `none` như cũ (câu không tiền không thành câu tiền).
 *
 * Tiền LUÔN `bigint` minor units (CLAUDE.md §3.1). File này KHÔNG import contracts/registry nào —
 * keep-set `domain/ai` phải compile độc lập (index.md US-AIV2-0002 §2).
 */

export const MONEY_PARSE_REASON = Object.freeze({
  EXTRACTED: "EXTRACTED",
  NO_MATCH: "NO_MATCH",
  /** ≥2 giá trị KHÁC nhau trong câu ⇒ không đoán; `candidates` liệt kê đích danh. */
  AMBIGUOUS_MULTI_VALUE: "AMBIGUOUS_MULTI_VALUE",
  /** Số không đơn vị đứng trước danh từ đếm ("30 người") ⇒ số lượng, không phải tiền. */
  COUNT_NOUN: "COUNT_NOUN",
  /** Số trông như NĂM (19xx/20xx) không đơn vị ⇒ không phải tiền. */
  YEAR_LIKE: "YEAR_LIKE",
  /** Số TRẦN <4 chữ số ("2", "500") hoặc số của KỲ ("tháng 7") ⇒ không phải tiền (chép từ `@SEC-WRONG-02` v1). */
  BARE_SMALL_OR_PERIOD: "BARE_SMALL_OR_PERIOD",
  /** Số + từ chỉ độ lớn CHƯA RÕ BẬC ("2 trăm", "3 chục", "…rưỡi") ⇒ bỏ CẢ CÂU, hỏi lại. */
  AMBIGUOUS_MAGNITUDE_WORD: "AMBIGUOUS_MAGNITUDE_WORD",
  /**
   * 🔴 AI-INTERP N1 — câu có số tiền NHƯNG còn một đề cập số CHƯA RÕ VAI ("chi 300 tiền sân, 50k nước" · "3 lít" · "2m"), hoặc HAI khoản cùng
   * giá trị ("Lan chi 200k Hùng chi 200k") ⇒ không đoán; hỏi TỰ DO (không có tập ứng viên đúng để bày chip).
   */
  AMBIGUOUS_MENTIONS: "AMBIGUOUS_MENTIONS",
} as const);

export type MoneyParseReason = (typeof MONEY_PARSE_REASON)[keyof typeof MONEY_PARSE_REASON];

export type MoneyParseResult =
  | { readonly kind: "resolved"; readonly amountMinor: bigint }
  | {
      readonly kind: "ambiguous";
      readonly reason: typeof MONEY_PARSE_REASON.AMBIGUOUS_MULTI_VALUE;
      /** Các giá trị khác nhau tìm thấy, theo thứ tự xuất hiện — để HỎI, không để chọn hộ. */
      readonly candidates: readonly bigint[];
    }
  | {
      readonly kind: "ambiguous";
      readonly reason: typeof MONEY_PARSE_REASON.AMBIGUOUS_MAGNITUDE_WORD | typeof MONEY_PARSE_REASON.AMBIGUOUS_MENTIONS;
      /** Token gây mơ hồ ("trăm"/"chục") — chỉ token, KHÔNG nguyên câu (tối thiểu-cần-thiết tại nguồn). */
      readonly token: string | null;
    }
  | { readonly kind: "none" };

/**
 * Token tiền: số (có thể chứa . ,) + đơn vị tuỳ chọn. Lookahead `(?=$|[^\p{L}])` thay `\b` để đơn vị
 * không ăn đầu một từ khác — biện pháp sau bug *"400 tiền nước" → 400 TỶ*.
 *
 * 🔴 US-AIV2-0110 S29 (@UC0281): dấu `.`/`,` chỉ thuộc SỐ khi ngay sau nó là một chữ số. Bản cũ `(\d[\d.,]*)` nuốt cả dấu câu
 * ⇒ *"thu 200.000, chi 100.000"* có token "200.000," — không đọc được ⇒ bị bỏ ⇒ câu ra **100.000** tự tin (lưới nhiều giá trị
 * mù một nửa); *"chi 1.500.000."* ra không đọc được. Số KHÔNG được mở ngay sau một chữ số, cũng không ngay sau một dấu `.`/`,`
 * mà trước dấu đó KHÔNG phải chữ cái: chặn đọc nửa sau của một số viết sai ("1..5 triệu" · ".5tr" KHÔNG thành 5 triệu) nhưng vẫn
 * tách "200k,100k" · "1tr.2tr" thành hai số (rà đối kháng S29: bản đầu `(?<![\d.,])` làm rơi số thứ hai ⇒ "thu 200k,100k" ra
 * 200.000 tự tin). `(?![.,]?\d)` cấm regex LÙI vào giữa số khi chữ dính sau không phải đơn vị ("150,05trăm nghìn" từng khớp
 * "150,0" ⇒ 150 đ — property lưới S29 đo ra).
 */
/**
 * 🔴 AI-INTERP N4 (@UC0162): "N trăm/chục" + ĐƠN VỊ BẬC ngay sau ("2 trăm nghìn" · "5 trăm k" · "3 chục nghìn") là MỘT token có đơn vị — một số rõ
 * (×100 / ×10 rồi ×bậc, `parseVndAmountMinor`), như dạng chữ "hai trăm nghìn". Trước: token dừng ở "2", phần "trăm nghìn" rơi vào luật "N trăm" ⇒ hỏi
 * thừa, và bộ che `llm-amount-mask` biến "chi 2 trăm nghìn" thành "chi 200000 nghìn" (lệch 1.000×). "N trăm/chục" TRẦN (không bậc theo sau) vẫn HỎI.
 */
const HUNDRED_COMPOUND_UNIT = "(?:trăm|tram|chục|chuc)\\s*(?:nghìn|nghin|ngàn|ngan|triệu|trieu|củ|cu|tỉ|tỷ|ti|ty|k|tr)";
const MONEY_TOKEN_RE = new RegExp(
  `(?<!\\d)(?<!\\d[.,]+)(?<!(?:^|\\s)[.,])(\\d(?:[.,]?\\d)*)(?![.,]?\\d)\\s*(${HUNDRED_COMPOUND_UNIT}|nghìn|nghin|ngàn|ngan|triệu|trieu|củ|cu|tỉ|tỷ|ti|ty|k|tr|đồng|dong|vnđ|vnd|đ|d)?(?=$|[^\\p{L}])`,
  "giu",
);

/** NĂM (19xx/20xx) KHÔNG PHẢI TIỀN — chỉ áp cho số KHÔNG có đơn vị ("2026k" vẫn là 2.026.000 đ). */
const YEAR_LIKE_RE = /^(19|20)\d{2}$/;

/**
 * Danh từ ĐẾM đứng ngay sau một SỐ KHÔNG ĐƠN VỊ ⇒ số lượng ("cho 30 người", "3 lần"). EXPORT (AI-REDUNDANT-ASK, @UC0162): ô tiền đọc thẳng "1tr2"
 * chỉ khi CÂU không có danh từ đếm ngay sau span ("1tr2 người") — một định nghĩa danh từ đếm, không chép.
 *
 * 🔴 AI-INTERP N3 (@UC0162): tập NGƯỜI lấy từ từ vựng chung (`HEAD_COUNT_NOUNS` — có dấu VÀ không dấu: "1tr2 nguoi" · "1tr2 bạn" · "1tr2 bé" từng đọc
 * thẳng 1.200.000). Phần RIÊNG của bộ đổi tiền — đơn vị kỳ/việc — chỉ CÓ DẤU: dạng không dấu của chúng là tên/trạng từ thường gặp sau một số tiền
 * ("đóng 1tr2 Lan" · "Nam" · "chuyển 1tr2 ngay"). Ranh giới cuối là Unicode (`(?![\p{L}\p{N}])`): `\b` với cờ `u` là ranh giới ASCII ⇒ "quỹ" · "kỳ" (chữ cuối
 * có dấu) từng KHÔNG BAO GIỜ khớp. "em"/"con"/"hộ"/"nhà" cố ý VẮNG (đại từ/giới từ sau số tiền — `PRONOUN_LIKE_PERSON_NOUNS`).
 */
// "quỹ" cố ý VẮNG (gỡ ở N3): nó chưa từng khớp (lỗi `\b`), và ở mọi chỗ nó sẽ khớp đều sai — "nộp 1tr 2 quỹ lớp" thành 1.000.000 tự tin, "đóng 1tr2 quỹ
// lớp" bị hỏi oan. Không ai đếm "2 quỹ" sau một số tiền.
const PERIOD_OR_ITEM_COUNT_NOUNS = ["lần", "đợt", "tháng", "tuần", "năm", "ngày", "nhóm", "khoản", "bậc", "kỳ"] as const;
const HEAD_COUNT_ALT = countNounAlternation(countNounForms(HEAD_COUNT_NOUNS, { plain: true }));
export const COUNT_NOUN_AFTER_NUMBER_RE = new RegExp(`^\\s*(?:${HEAD_COUNT_ALT}|${countNounAlternation(PERIOD_OR_ITEM_COUNT_NOUNS)})(?![\\p{L}\\p{N}])`, "iu");
/**
 * 🔴 vá review CRITICAL C2 (@UC0162) — danh từ người CHẮC CHẮN là số đầu người cả khi số đứng trước là một SỐ TIỀN trần (≥4 chữ số) hay một ĐUÔI GHÉP
 * ("1 triệu 2 …"): CHỈ "người"/"thành viên" viết CÓ DẤU — đúng tập của develop. Bản N3 dùng cả tập người, cả dạng không dấu ⇒ «nộp 500.000 ban đại diện,
 * 50k quỹ lớp» · «chi 500000 dua thay Hung, 100000 mua nuoc» ra số NHỎ tự tin, «đóng 300.000 ban phụ huynh» ra `none`; và dạng CÓ dấu cũng sai: sau một số
 * tiền, "bạn/bé/cháu/đứa" thường là người NHẬN («nộp 500.000 bạn Lan, 50k quỹ lớp» ⇒ 50.000). Ngoài tập này, số ≥4 chữ số là ứng viên tiền như develop
 * (câu hai số ⇒ HỎI kèm chip); "1500 người" vẫn là số người. "200000 tháng này" · "150.000 kỳ này" là TIỀN (đơn vị kỳ không bỏ số ≥4 chữ số).
 */
const CERTAIN_HEAD_COUNT_NOUNS = HEAD_COUNT_NOUNS.filter((n) => n.vi === "người" || n.vi === "thành viên");
const CERTAIN_HEAD_COUNT_NOUN_AFTER_RE = new RegExp(`^\\s*(?:${countNounAlternation(countNounForms(CERTAIN_HEAD_COUNT_NOUNS, { plain: false }))})(?![\\p{L}\\p{N}])`, "iu");
const PERIOD_COUNT_NOUN_AFTER_RE = new RegExp(`^\\s*(?:${countNounAlternation(PERIOD_OR_ITEM_COUNT_NOUNS)})(?![\\p{L}\\p{N}])`, "iu");

/**
 * "N trăm" / "N chục" (có dấu hoặc không) ⇒ bậc không xác định (trăm đồng? trăm nghìn?) ⇒ HỎI.
 * Câu có đơn vị trực tiếp ("1,5tr", "150k") không bị luật này chạm.
 */
const AMBIGUOUS_MAGNITUDE_AFTER_NUMBER_RE = /^\s*(trăm|tram|chục|chuc)(?=$|[^\p{L}])/iu;

/** Số của KỲ đứng ngay trước: "tháng 7" · "thang 7" · "T7" · "T07" — con số này KHÔNG phải tiền (`@SEC-WRONG-02` v1). */
const PERIOD_WORD_BEFORE_NUMBER_RE = /(?:^|[^\p{L}])(?:tháng|thang|t)\s*$/iu;
/** Số trần là tiền chỉ khi ≥4 chữ số — "100.000" (6) đạt, "500" / "2" không (`@SEC-WRONG-02` v1). */
const BARE_MIN_DIGITS = 4;

/**
 * Số trần đứng NGAY SAU một số-có-đơn-vị ("1 triệu **2**", "1tr**2**", "2 triệu **500**") là ĐUÔI SỐ GHÉP —
 * người nói 1.200.000 hay 1.000.002? Không biết ⇒ HỎI (review PR #796 HIGH-2: bỏ số nhỏ mù quáng biến ca này
 * thành `resolved 1.000.000` tự tin — sai chiều ngược của chính luật "không chọn con số mình thích").
 */
// Nguồn DUY NHẤT ở `vi-number-words.ts` (bộ viết lại dùng CHÍNH vị-từ này cho "1tr hai" — US-AIV2-0110 S18).


/**
 * US-AIV2-0022 (LOW-6 #796) — số trần là MỘT NỬA của hình ngày `dd/mm` · `dd-mm` (· `dd/mm/yyyy`): nửa đầu khi NGAY SAU nó
 * là `/`|`-` rồi 1–2 chữ số; nửa sau khi NGAY TRƯỚC nó là 1–2 chữ số rồi `/`|`-`. Hai vị-từ, không có vế nào khớp
 * "1 triệu 2" (không có `/`|`-`), nên luật đuôi ghép vẫn nguyên cho ca đó. Chỉ số ≤2 chữ số mới là "ngày/tháng":
 * "200k 15/9" đúng; "1tr 2026/9" KHÔNG đi qua vế này (`2026` >2 chữ số) — nó bị `YEAR_LIKE_RE` bỏ TRƯỚC đó và "9" là số trần
 * nhỏ ⇒ kết quả vẫn `resolved 1.000.000` nhưng qua luật khác (review #815 L5: khai đúng cơ chế, đừng đọc thành "hỏi lại").
 */
const DATE_HALF_AFTER_RE = /^[/-]\d{1,2}(?!\d)/u;
const DATE_HALF_BEFORE_RE = /(?:^|[^\d.,])\d{1,2}[/-]$/u;

/** Đơn vị tiền của token (cùng tập với `MONEY_TOKEN_RE`), dùng trong các vị-từ "số trần đứng cạnh số-có-đơn-vị" dưới. */
const UNIT_ALT = `${HUNDRED_COMPOUND_UNIT}|nghìn|nghin|ngàn|ngan|triệu|trieu|củ|cu|tỉ|tỷ|ti|ty|k|tr|đồng|dong|vnđ|vnd|đ|d`;

/**
 * 🔴 US-AIV2-0110 S29 (@UC0281, rà đối kháng) — số trần đứng NGAY TRƯỚC một số-có-đơn-vị theo năm hình dạng mà số-có-đơn-vị một
 * mình đọc SAI (lấy nửa sau, bỏ nửa đầu) ⇒ bỏ CẢ CÂU, hỏi lại:
 *   · KHOẢNG / LỰA CHỌN dùng chung đơn vị: "1.5-2tr" · "1-2tr" · "từ 1.5 đến 2 triệu" · "1,5 hay 2 củ" (từng ra 2.000.000);
 *   · dấu phân cách + KHOẢNG TRẮNG: "1. 5 triệu" · "thu 1, 5tr" (từng ra 5.000.000);
 *   · số có dấu + khoảng trắng + số có đơn vị: "1,2 5 triệu" (= "1 phẩy 2 5 triệu", từng ra 5.000.000);
 *   · khoảng trắng làm dấu nhóm nghìn: "12 500đ" · "1 500k" · "1 500 000 đ" (từng ra 500 · 500.000). Giá: "đợt 2 300k" cũng hỏi.
 */
/**
 * Từ NỐI khoảng / lựa chọn / sửa số ("1.5-2tr" · "từ 1,5 đến 2 triệu" · "1 hay là 2 triệu" · "1,5 chứ không phải 2 triệu" ·
 * "tăng từ 1,5 lên 2 triệu") — số trần đứng trước từ nối DÙNG CHUNG đơn vị của số sau ⇒ hỏi, không lấy riêng số sau (rà vòng 1–3).
 */
const CONNECTOR_ALT =
  "->|=>|[-–—~→/]|(?:đến|tới)\\s+tận|(?:den|toi)\\s+tan|đến|den|tới|toi|hoặc(?:\\s+là)?|hoac(?:\\s+la)?|hay(?:\\s+là|\\s+la)?|" +
  "(?:lên|len|xuống|xuong)(?:\\s+(?:còn|con|thành|thanh))?|thành|thanh|với|voi|và|va|" +
  "(?:(?:chứ|chớ|chu|cho)\\s+)?(?:không|ko|k|khong|đâu|dau)\\s+(?:phải|phai)(?:\\s+(?:là|la))?|thay\\s+vì|thay\\s+vi|cho\\s+(?:đến|den|tới|toi)";
const RANGE_TO_UNIT_NUMBER_RE = new RegExp(`^\\s*(?:(?:thôi|thoi|nhé|nhe|thì|thi|đó|do)\\s+)?(?:${CONNECTOR_ALT})\\s*\\d[\\d.,]*\\s*(?:${UNIT_ALT})(?![\\p{L}])`, "iu");
const SEP_SPACE_UNIT_NUMBER_RE = new RegExp(`^\\s*[.,]\\s+\\d[\\d.,]*\\s*(?:${UNIT_ALT})(?![\\p{L}])`, "iu");
const SPACE_UNIT_NUMBER_RE = new RegExp(`^\\s+\\d[\\d.,]*\\s*(?:${UNIT_ALT})(?![\\p{L}])`, "iu");
const SPACE_GROUP_UNIT_NUMBER_RE = new RegExp(`^\\s+\\d{3}(?:\\s\\d{3})*\\s*(?:${UNIT_ALT})(?![\\p{L}])`, "iu");
/** "1 500 000 tiền sân" — ≥2 nhóm 3 chữ số ngăn bằng khoảng trắng, không đơn vị. */
const SPACE_GROUPS_RE = /^(?:\s\d{3}){2,}(?!\d)/u;
/**
 * PHÉP NHÂN/CHIA sát một số-có-đơn-vị ⇒ tổng hay đơn giá? (S29b M1 · S29c rà vòng 6). "500k xe ôm" không phải phép nhân;
 * "chia đều" là luật của trang chia (EXP-F-EQUAL/COL-EQUAL), không phải phép tính ⇒ không tính.
 *   · sau: "1.5tr*2" · "500k nhân 2" · "1tr chia 3" · "500k (x2)" · "1.5tr/bé x 2 bé"
 *   · trước: "2 x 1.5tr" · "3 bé x 500k" · "4 buổi * 300k" · "gấp đôi 500k" · "đơn giá 150k" · "chia 2 1tr"
 */
// (toán tử phải đứng NGAY trước chữ số ⇒ "500k xe ôm" · "300k chia đều 3 người" tự không khớp — không cần loại trừ riêng)
const MULTIPLY_OP = "[*×]|x|nhân|nhan|chia|gấp";
const MULTIPLY_AFTER_RE = new RegExp(`^\\s*(?:\\/\\s*\\p{L}+\\s*)?\\(?\\s*(?:${MULTIPLY_OP})\\s*\\d`, "iu");
const MULTIPLY_BEFORE_RE = /(?:\d\s*(?:\p{L}+\s+){0,2}(?:[*×]|(?<![\p{L}])x)|(?<![\p{L}])(?:gấp|gap)\s+(?:đôi|doi|rưỡi|ruoi|ba|bốn|bon|năm|nam|hai|\d+)(?:\s+(?:lần|lan))?|(?<![\p{L}])(?:đơn|don)\s+(?:giá|gia)\s*:?|(?<![\p{L}])(?:nhân|nhan|chia)\s*\d+[.,]?)\s*$/iu;
/** Đơn vị tiền dính ngay sau một số (để phân biệt "1.5tr" với "1.5M"/"2.5kg"). */
const UNIT_AT_START_RE = new RegExp(`^(?:${UNIT_ALT})(?![\\p{L}])`, "iu");
/** "2tr,5" · "1tr.2" — đuôi ghép sau số-có-đơn-vị, ngăn bằng MỘT dấu (không khoảng trắng) ⇒ hỏi như "2tr5". */
const COMPOUND_HEAD_SEP_BEFORE_NUMBER_RE = new RegExp(`\\d\\s*(?:${UNIT_ALT})[.,]$`, "iu");
/** Bậc GHÉP bằng chữ số ("1 nghìn tỷ" · "1.5 nghìn tỷ" — từng ra 1.000 · 1.500 đ, lệch 10⁹) ⇒ hỏi. Chỉ "tỉ lệ" không phải bậc. */
const STACKED_MAGNITUDE_AFTER_RE = /^\s*(tỷ|tỉ|triệu|trieu)(?![\p{L}])(?!\s*(?:lệ|le)(?![\p{L}]))(\s+\p{L})?/iu;
/** Sau nghìn/ngàn: "ty"/"ti" không dấu chỉ có thể là tỷ ("1 nghin ty"); sau nghìn/ngàn/k: "củ" (= triệu) ⇒ "2 nghìn củ" = 2 tỷ. */
const STACKED_TY_AFTER_THOUSAND_RE = /^\s*(ty|ti|tr)(?![\p{L}])(?!\s*le(?![\p{L}]))/iu;
const STACKED_CU_AFTER_THOUSAND_RE = /^\s*(củ|cu)(?![\p{L}])/iu;
const THOUSAND_WORD_RE = /^(nghìn|nghin|ngàn|ngan)$/iu;
const THOUSAND_UNIT_RE = /^(nghìn|nghin|ngàn|ngan|k)$/iu;

/**
 * Token có đơn vị đứng trước một bậc nữa ⇒ bậc ghép. Miễn trừ DUY NHẤT: họ "Triệu" viết hoa đầu NGAY TRƯỚC một từ viết hoa đầu
 * ("thưởng 500k Triệu Vy" — rà vòng 2). Rà vòng 3: bản trước tha MỌI từ bậc viết hoa ("Vốn 1 Nghìn Tỷ" ra 1.000) và tha "so" ("1.5
 * nghìn tỷ so với kế hoạch" ra 1.500) — cả hai đã gỡ.
 */
function isStackedMagnitude(unit: string, after: string): boolean {
  const hit = STACKED_MAGNITUDE_AFTER_RE.exec(after);
  if (hit !== null) {
    const word = hit[1]!;
    const nextWord = /^\s*\S+\s+(\S+)/u.exec(after)?.[1] ?? "";
    const isSurname = /^Tri[ệe]u$/u.test(word) && /^\p{Lu}/u.test(nextWord) && !/^(?:đồng|dong|vnđ|vnd|đ)$/iu.test(nextWord);
    return !isSurname;
  }
  if (THOUSAND_WORD_RE.test(unit) && STACKED_TY_AFTER_THOUSAND_RE.test(after)) return true;
  return THOUSAND_UNIT_RE.test(unit) && STACKED_CU_AFTER_THOUSAND_RE.test(after);
}

/** US-AIV2-0112 H04 — số-có-đơn-vị DÍNH LIỀN ngay trước (không khoảng trắng giữa đơn vị và số sau): "1tr|2", "500k|3". */
const COMPOUND_HEAD_GLUED_BEFORE_NUMBER_RE = new RegExp(`\\d\\s*(?:${UNIT_ALT})$`, "iu");

/** "1 triệu lẻ 500 nghìn" · "1 triệu lẻ 500" · "1tr linh 5" — số nối bằng lẻ/linh sau số-có-đơn-vị là MỘT số ⇒ hỏi (rà vòng 2–3). */
const UNIT_NUMBER_LINKED_BEFORE_RE = new RegExp(`\\d\\s*(?:${UNIT_ALT})\\s+(?:lẻ|le|linh)\\s*$`, "iu");

/** "từ nửa đến 1 triệu" · "nửa hay là 1 triệu" · "nửa chứ không phải 1 triệu" (rà vòng 2–3: từng ra 1.000.000). */
const HALF_RANGE_RE = new RegExp(`(?:^|[^\\p{L}])(?:nửa|nua)\\s*(?:${CONNECTOR_ALT})\\s*\\d[\\d.,]*\\s*(?:${UNIT_ALT})(?![\\p{L}])`, "iu");
/** "300k nua den 30/9" — "nua" ngay sau một số-có-đơn-vị là "nữa", không phải "nửa" (rà vòng 4, hỏi oan). */
const UNIT_NUMBER_BEFORE_HALF_RE = new RegExp(`\\d\\s*(?:${UNIT_ALT})\\s+(?:nửa|nua)\\s*(?:${CONNECTOR_ALT})`, "iu");
/** "chi hai hay ba triệu" · "chi một đến hai triệu" — SỐ BẰNG CHỮ đứng một mình + từ nối + số-có-đơn-vị (rà vòng 4). */
const WORD_NUMBER_RANGE_RE = new RegExp(
  `(?:^|[^\\p{L}])(?:một|mot|hai|ba|bốn|bon|năm|nam|sáu|sau|bảy|bay|tám|tam|chín|chin|mười|muoi)\\s+(?:${CONNECTOR_ALT})\\s*\\d[\\d.,]*\\s*(?:${UNIT_ALT})(?![\\p{L}])`,
  "iu",
);
/** "chi 1 triệu hay 2" · "thu 200k-300" — số-có-đơn-vị + từ nối + số TRẦN (dùng chung đơn vị) ⇒ hỏi. */
// Từ nối HẸP (không "/" · "và" · "thành": "300k/2 người" · "200k và 2 bạn" không phải khoảng).
const UNIT_RANGE_TO_BARE_RE = new RegExp(`^\\s*(?:->|[-–—~→]|đến|den|tới|toi|hay|hoặc|hoac)\\s*\\d[\\d.,]*(?!\\s*(?:${UNIT_ALT})(?![\\p{L}]))(?![\\d.,/-])`, "iu");
/**
 * Chữ số mà `MONEY_TOKEN_RE` cố ý KHÔNG mở (sau "khoảng trắng + dấu" hay sau "hai dấu liền"): ".5 triệu" · "200k ,300k" ·
 * "200.000,,300.000" ⇒ không đọc được, nhưng KHÔNG được vô hình — rà vòng 4: "chi 200k ,300k" từng ra 200.000 tự tin.
 */
const HIDDEN_NUMBER_RE = /\d[.,]{2,}\d|(?:^|\s)[.,]\d/u;

/** "20.00" · "19.30" · "8.30": giờ.phút hợp lệ (dấu CHẤM + đúng 2 chữ số). */
function isTimeLike(numPart: string): boolean {
  const m = /^(\d{1,2})\.(\d{2})$/u.exec(numPart);
  return m !== null && Number(m[1]) <= 23 && Number(m[2]) <= 59;
}

/** "30.9" · "25.10": ngày.tháng hợp lệ (dấu CHẤM). */
function isDayMonthLike(numPart: string): boolean {
  const m = /^(\d{1,2})\.(\d{1,2})$/u.exec(numPart);
  return m !== null && Number(m[1]) >= 1 && Number(m[1]) <= 31 && Number(m[2]) >= 1 && Number(m[2]) <= 12;
}

/** Số trần có MỘT dấu và 1–2 chữ số sau dấu ("1,5" · "1.5" · "20.00") — hình thập phân. */
function isBareDecimal(numPart: string): boolean {
  return /^\d+[.,]\d{1,2}$/u.test(numPart);
}

/**
 * 🔴 Ngữ cảnh làm số thập phân TRẦN không phải tiền — tập ĐÓNG, HẸP, và MỖI TỪ LÀ MỘT LỖ: có mặt ⇒ số thập phân bị bỏ qua ⇒ số tiền
 * khác trong câu THẮNG. Rà vòng 3–4 đo hàng chục câu lọt qua bản rộng hơn ("hôm qua chi 1,5 chứ không phải 2 triệu" · "trả TRƯỚC 1.5,
 * còn 2 triệu" · "LỆ phí 1,5, học phí 2 triệu" · "điểm danh: Lan 1.5, Hùng 2 triệu"). Thiếu một từ ở đây ⇒ HỎI THỪA (an toàn).
 * ĐỪNG thêm từ nào có thể đứng trước/sau một số TIỀN; từ ngữ cảnh phải đứng NGAY cạnh số.
 *   · ĐƠN VỊ ĐO / thời lượng / % dính ngay sau: "lãi 1,5%" · "2.5 kg" · "1,5 cân" · "8,5 điểm" · "1,5 tiếng" · "1.5h" · "2,5 ngày".
 *     KHÔNG "m"/"l"/"lít": tiếng lóng tiền ("1.5m" = 1,5 triệu · "2,5 lít" = 250k) — rà vòng 4.
 *   · GIỜ.phút (dấu chấm) với từ giờ NGAY trước (≤2 từ: lúc/họp/sáng/chiều/tối/trưa/nay/mai) hoặc NGAY sau (h/sáng/tối/thứ…).
 *   · NGÀY.tháng (dấu chấm) với "hạn"/"chót"/"ngày"/"mùng" NGAY trước.
 *   · Nhãn NGAY trước: "tỉ lệ"/"tỷ lệ" · "điểm" · "phiên bản"/"version" · "lớp"/"lop" (tên lớp "6.1" — dấu CHẤM).
 */
const DECIMAL_MEASURE_AFTER_RE =
  /^\s*(?:%|(?:kg|g|gam|ml|km|cm|mm|mét|cân|ký|kí|lạng|tấn|điểm|độ|tuổi|tiếng|tieng|h|giờ|gio|phút|phut|buổi|buoi|ngày|tuần|tuan)(?![\p{L}]))/u; // phân biệt HOA: "1.5M" không phải mét
const TIME_WORD_AFTER_RE = /^\s*(?:h|giờ|gio|sáng|chiều|chieu|tối|trưa|trua|đêm|dem|thứ)(?![\p{L}])/iu;
const TIME_WORDS_BEFORE = new Set(["lúc", "luc", "họp", "hop", "sáng", "chiều", "chieu", "tối", "trưa", "trua", "nay", "mai"]);
const DATE_WORDS_BEFORE = new Set(["hạn", "han", "chót", "chot", "ngày", "mùng", "mung"]);

function isNonMoneyDecimalContext(before: string, after: string, numPart: string): boolean {
  if (DECIMAL_MEASURE_AFTER_RE.test(after)) return true;
  const words = before.toLowerCase().match(/\p{L}+/gu) ?? [];
  const last = words[words.length - 1] ?? "";
  const last2 = words.slice(-2).join(" ");
  const adjacent = /\p{L}\s*$/u.test(before); // từ ngữ cảnh phải dính ngay trước số (không qua dấu câu)
  if (adjacent && (["tỉ lệ", "tỷ lệ", "ty le", "phiên bản", "phien ban"].includes(last2) || ["điểm", "diem", "version"].includes(last))) return true;
  if (adjacent && ["lớp", "lop", "mục", "muc"].includes(last) && numPart.includes(".")) return true;
  if (isTimeLike(numPart) && ((adjacent && words.slice(-2).some((w) => TIME_WORDS_BEFORE.has(w))) || TIME_WORD_AFTER_RE.test(after))) return true;
  return isDayMonthLike(numPart) && adjacent && DATE_WORDS_BEFORE.has(last);
}

/** "30.09.2026" · "1.5.2026" — ngày đầy đủ, không phải số có dấu không đọc được (rà vòng 3: "hạn 30.09.2026 đóng 200k" từng hỏi oan). */
const FULL_DATE_RE = /^\d{1,2}[./]\d{1,2}[./](?:\d{2}|\d{4})$/u;

/**
 * Số trần (không đơn vị) đứng sát một số-có-đơn-vị theo hình mà số-có-đơn-vị một mình đọc sai. Miễn trừ (hỏi oan): số đầu dòng
 * (có thể có gạch đầu dòng) + dấu + số ≥3 chữ số = đánh số danh sách ("1. 500k tiền sân"); giờ.phút ("họp lúc 19.30 100k/người").
 */
/**
 * 🔴 AI-INTERP vá security-review H1 (ReDoS) — mọi vị-từ neo `$` chỉ chạy trên CỬA SỔ ngắn ngay trước token (`TOKEN_CONTEXT_WINDOW` ký tự, bỏ nửa từ bị
 * cắt), vị-từ neo `^` chỉ trên cửa sổ ngay sau. Trước: chạy trên CẢ tiền tố câu, hai `\s*` kề + `\n` ∈ `\s` ⇒ ~O(n³) MỖI số trần nhỏ — "\n"×1000 + "x" +
 * " 1"×20 mất 31,6 s (develop 5,3 s). Không luật nào cần nhìn xa hơn vài từ. "Đầu dòng" (đánh số danh sách) đo trên DÒNG hiện tại (`lastIndexOf`), không
 * bằng regex trên tiền tố; "câu không dấu" đo MỘT lần cho cả câu.
 */
const TOKEN_CONTEXT_WINDOW = 64;
/**
 * 🔴 vá review bảo mật LOW-C — cửa sổ đếm ký tự THÔ ⇒ ≥64 khoảng trắng giữa số và chữ kề tắt luật kề ("chi 1 nghìn" + 70 dấu cách + "tỷ" ra 1.000 đ).
 * Một dãy khoảng trắng liền nhau là MỘT ranh giới: có xuống dòng ⇒ "\n" (giữ luật "đầu dòng"), không ⇒ " ". Gộp ở ĐẦU `extractMoneyCandidates` (hàm không
 * trả vị trí nào trên câu gốc). Cũng bỏ mọi chi phí O(w²) của regex có `\s*` trên dãy dài.
 */
/**
 * 🔴 vá review bảo mật LOW-D — ký tự định dạng VÔ HÌNH (`\p{Cf}`: ZWSP · ZWJ · soft hyphen · word joiner · BOM · dấu hướng chữ) cắt token mà người đọc không
 * thấy: «chi 1tr\u200D2 tháng» (ZWJ) ra 1.000.000 trong khi «chi 1tr2 tháng» hỏi; «chi 2 tr\u200Băm» ra 2.000.000; «chi 1 nghìn\u200Btỷ» ra 1.000. Bỏ MỌI `\p{Cf}` của câu
 * (không chỉ cạnh chữ số như `FORMAT_CHAR_BY_DIGIT_RE` của bộ cạo PII — "tr\u200Băm" · "nghìn\u200Btỷ" không kề chữ số nào) ⇒ bộ đổi tiền đọc đúng câu người đọc thấy.
 * Ở bộ đọc, KHÔNG ở DTO: câu lưu/gửi LLM giữ nguyên (bộ cạo PII có luật riêng).
 */
const FORMAT_CHAR_RE = /\p{Cf}/gu;

function collapseWhitespace(input: string): string {
  return input.replace(/\s+/gu, (ws) => (ws.includes("\n") ? "\n" : " "));
}

/** Dòng hiện tại ≤ vài ký tự trống/gạch đầu dòng ⇒ số đứng ĐẦU DÒNG (đánh số danh sách "1. 500k tiền sân" · "- 2) …"). */
const LIST_LEAD_RE = /^[ \t]*(?:[-*•+][ \t]*)?$/u;
const LIST_LEAD_MAX = 8;

interface TokenContext {
  /** Token đứng ĐẦU DÒNG (chỉ có khoảng trắng / gạch đầu dòng trước nó trên dòng). */
  readonly atListStart: boolean;
  /** Cả câu gõ KHÔNG dấu. */
  readonly sentenceUnaccented: boolean;
}

function windowBefore(message: string, index: number): string {
  const start = Math.max(0, index - TOKEN_CONTEXT_WINDOW);
  const w = message.slice(start, index);
  return start > 0 && /[\p{L}\p{N}]/u.test(message[start - 1] ?? "") ? w.replace(/^[\p{L}\p{N}]+/u, "") : w;
}

function windowAfter(message: string, from: number): string {
  const end = Math.min(message.length, from + TOKEN_CONTEXT_WINDOW);
  const w = message.slice(from, end);
  return end < message.length && /[\p{L}\p{N}]/u.test(message[end] ?? "") ? w.replace(/[\p{L}\p{N}]+$/u, "") : w;
}

function atListStartOf(message: string, index: number): boolean {
  const line = message.slice(message.lastIndexOf("\n", index - 1) + 1, index);
  return line.length <= LIST_LEAD_MAX && LIST_LEAD_RE.test(line);
}

function bareNumberJoinsNextAmount(numPart: string, after: string, before: string, ctx: TokenContext): boolean {
  // (từ nối gồm cả "/": "1/2 triệu" là PHÂN SỐ, không phải ngày — rà vòng 3: từng ra 2.000.000)
  if (RANGE_TO_UNIT_NUMBER_RE.test(after)) return true;
  // khoảng trắng nhóm nghìn chỉ sau một đầu nhóm THUẦN chữ số 1–3 ("12 500đ" · "1 500 000 tiền sân")
  if (/^\d{1,3}$/u.test(numPart) && (SPACE_GROUP_UNIT_NUMBER_RE.test(after) || SPACE_GROUPS_RE.test(after))) return true;
  const listItem = ctx.atListStart && /^\s*[.,]\s+\d{3}/u.test(after);
  // "đợt 1, 300k mỗi người" · "tuần 2, 150k" — số THỨ TỰ của đợt/tuần/lần + dấu phẩy câu (rà vòng 4, hỏi oan).
  const ordinal = /(?:^|[^\p{L}])(?:đợt|dot|tuần|tuan|lần|lan|kỳ|ky|khóa|khoa|tổ|nhóm|nhom)\s*$/iu.test(before) && /^[.,]\s+\d{2,}/u.test(after);
  if (SEP_SPACE_UNIT_NUMBER_RE.test(after) && !listItem && !ordinal) return true;
  return /[.,]/u.test(numPart) && !isTimeLike(numPart) && SPACE_UNIT_NUMBER_RE.test(after);
}

/** Số trần `numPart` (đã khớp `MONEY_TOKEN_RE`, không đơn vị) có phải một nửa của hình ngày không. */
function isDateHalf(numPart: string, before: string, after: string): boolean {
  if (numPart.length > 2 || !/^\d+$/u.test(numPart)) return false;
  return DATE_HALF_AFTER_RE.test(after) || DATE_HALF_BEFORE_RE.test(before);
}

type TokenVerdict =
  | { readonly kind: "skip" }
  | { readonly kind: "decimal-signal" }
  /** AI-INTERP N1: số trần nhỏ KHÔNG giải thích được (không kỳ/ngày/năm/giờ/số lượng/nhãn) — có thể là tiền viết tắt ("300" = 300k?). */
  | { readonly kind: "unclear"; readonly token: string }
  | { readonly kind: "value"; readonly value: bigint }
  | { readonly kind: "ambiguous"; readonly result: MoneyParseResult };

const SKIP: TokenVerdict = { kind: "skip" };
const ambiguousToken = (token: string | null): TokenVerdict => ({
  kind: "ambiguous",
  result: { kind: "ambiguous", reason: MONEY_PARSE_REASON.AMBIGUOUS_MAGNITUDE_WORD, token },
});

/**
 * Giá trị token có đơn vị. AI-INTERP N4: "N chục <bậc>" = 10 × "N <bậc>" — ×10 trên CHÍNH bộ đọc cụm (`parseVndAmountMinor` của "N <bậc>"), không phép
 * đọc thứ hai; bộ đọc cụm giữ "chục" là chữ lạ (`null`) cho làn ĐỌC (`answer/number-trace.ts` chặn đơn vị nói «5 chục nghìn» trong câu của model).
 */
function unitTokenValue(numPart: string, unit: string, token: string): bigint | null {
  const ten = /^(?:chục|chuc)\s*(\S+)$/iu.exec(unit);
  if (ten === null) return parseVndAmountMinor(token.trim());
  const base = parseVndAmountMinor(`${numPart} ${ten[1]!}`);
  return base === null ? null : base * 10n;
}

/** Token CÓ đơn vị. */
function classifyUnitToken(m: RegExpMatchArray, numPart: string, unit: string, before: string, after: string): TokenVerdict {
  // S29: "1 triệu 500 nghìn" · "1tr500k" · "1 triệu lẻ 500 nghìn" từng ra HAI ứng viên [1.000.000, 500.000] — chip nào cũng SAI
  // (người nói 1.500.000) ⇒ hỏi tự do, không bày chip. "1 nghìn tỷ" ⇒ bậc ghép ⇒ hỏi.
  if (COMPOUND_HEAD_BEFORE_NUMBER_RE.test(before) || UNIT_NUMBER_LINKED_BEFORE_RE.test(before)) return ambiguousToken(numPart);
  // "2 trăm nghìn tỷ": luật bậc ghép xét TỪ BẬC CUỐI của đơn vị ghép (N4).
  if (isStackedMagnitude(unit.split(/\s+/u).at(-1) ?? unit, after)) return ambiguousToken(numPart);
  if (UNIT_RANGE_TO_BARE_RE.test(after)) return ambiguousToken(numPart);
  // 🔴 S29b (M1): PHÉP NHÂN sát một số-có-đơn-vị ("1.5tr*2" · "2 x 1.5tr" · "500k × 3 người") — tổng hay đơn giá? ⇒ hỏi.
  if (MULTIPLY_AFTER_RE.test(after) || MULTIPLY_BEFORE_RE.test(before)) return ambiguousToken(numPart);
  const parsed = unitTokenValue(numPart, unit, m[0]);
  // S29 (rà vòng 2, HIGH): token CÓ ĐƠN VỊ mà không đọc được ("1,500k" · "1.500 triệu" · "200,000 đ" · "1,5 đồng") là tiền MƠ HỒ,
  // không phải "không phải tiền" ⇒ bỏ CẢ CÂU. Bỏ qua lặng lẽ thì số còn lại thắng tự tin ("Lan 1,500k, Hùng 500k" ⇒ 500.000).
  // Ngoại lệ HẸP: "dong" không dấu còn là "đóng" — CHỈ khi số là tên lớp/mục/giờ ("lop 10.2 dong 300k" — rà vòng 4). Rà vòng 5 (H2):
  // bản trước tha MỌI "…d"/"… dong" ⇒ "thu 150,000d tien quy va 50k" ra 50.000 (token tiền mơ hồ vứt lặng lẽ, số kia thắng).
  // Rà vòng 6 (HIGH): CHỈ xét ngữ cảnh TRƯỚC số — chữ SAU đơn vị ("150,000d TUẦN này" · "… dong NGÀY 5") từng khớp tập đơn vị đo ⇒
  // token bị bỏ lặng lẽ ⇒ số kia thắng. Và số có đủ nhóm 3 chữ số ("150,000") không bao giờ là tên lớp/mục/giờ.
  const dongLabel = /^(?:dong|d)$/u.test(unit) && !/\d[.,]\d{3}/u.test(numPart) && isNonMoneyDecimalContext(before, "", numPart);
  if (parsed === null) return dongLabel ? SKIP : ambiguousToken(numPart);
  return parsed > 0n ? { kind: "value", value: parsed } : SKIP;
}

/** Từ (chữ cái) của `word` khớp một mục: gõ CÓ dấu ⇒ đúng dạng có dấu; gõ KHÔNG dấu ⇒ dạng không dấu, trừ mục `accentedOnly`. Chữ viết hoa đầu ⇒ tên riêng, không khớp. */
function wordIsNoun(word: string, nouns: readonly CountNoun[], sentenceUnaccented: boolean): boolean {
  if (word === "" || /^\p{Lu}/u.test(word)) return false;
  const lower = word.normalize("NFC").toLowerCase();
  const plain = normalizeViText(lower);
  const typedPlain = plain === lower;
  // Câu gõ KHÔNG dấu toàn bộ: dạng không dấu là cách DUY NHẤT để gõ chữ đó ("nop 200k cham 2 ngay") ⇒ mục `accentedOnly` cũng tính.
  // Gõ ĐÚNG chính tả (kể cả chữ vốn không dấu: "con" · "ca") ⇒ khớp; gõ dạng bỏ dấu của chữ CÓ dấu ⇒ chỉ khi mục không `accentedOnly` hoặc cả câu không dấu.
  return nouns.some((n) => n.vi === lower || (typedPlain && n.plain === lower && (sentenceUnaccented || !n.accentedOnly)));
}

/**
 * Loại từ đếm sau số trần nhỏ: vật/đơn vị (`CLASSIFIER_NOUNS`) + NGƯỜI — "em"/"con"/"nhà" (vá review MED-1: "cho 3 em" · "cho 2 con" · "cho 2 nhà"; "hộ" cố
 * ý vắng — "đóng 300 hộ Lan" là giới từ) + vai xưng hô (`KINSHIP_PERSON_NOUNS`). Danh từ người chính (`HEAD_COUNT_NOUNS`) đã bỏ số ở luật đếm trước đó.
 */
const SMALL_NUMBER_CLASSIFIERS: readonly CountNoun[] = [
  ...CLASSIFIER_NOUNS,
  ...PRONOUN_LIKE_PERSON_NOUNS.filter((n) => n.vi !== "hộ"),
  ...KINSHIP_PERSON_NOUNS,
];

/** "tiền/phí/giá <nhãn> N": chữ sau "tiền" là MỤC ĐÍCH ("tiền sân 300"), không phải nhãn của số. */
const PURPOSE_HEAD_WORDS: ReadonlySet<string> = new Set(["tiền", "tien", "phí", "phi", "giá", "gia"]);

/**
 * 🔴 AI-INTERP N1 (@UC0209) — số trần NHỎ (<4 chữ số, nguyên) có vai KHÔNG-tiền GIẢI THÍCH được: loại từ/đơn vị đếm NGAY SAU ("3 ly" · "5 tuổi" ·
 * `CLASSIFIER_NOUNS`), hoặc nhãn NGAY TRƯỚC ("lớp 5" · "con thứ 2" · `LABEL_NOUNS_BEFORE_NUMBER` — trừ khi nhãn đứng sau "tiền/phí/giá"). Không giải
 * thích được ⇒ đề cập CHƯA RÕ VAI. Hai tập là danh sách CÁI ĐƯỢC THA (thiếu ⇒ hỏi thừa, an toàn).
 */
function isExplainedSmallNumber(before: string, after: string, ctx: TokenContext): boolean {
  // đơn vị đo / % dính sau ("tỷ lệ 100%" · "5kg" · "2 tiếng") · số thứ tự đầu dòng ("1. 500k tiền sân" · "- 2) …")
  if (DECIMAL_MEASURE_AFTER_RE.test(after) || (ctx.atListStart && /^[.)]/u.test(after))) return true;
  const unaccented = ctx.sentenceUnaccented;
  const next = /^\s*(\p{L}+)/u.exec(after)?.[1] ?? "";
  if (wordIsNoun(next, SMALL_NUMBER_CLASSIFIERS, unaccented)) return true;
  // nhãn có thể kèm "(" · "#" · ":" trước số ("lớp (12)" · "phòng #3" · "tổ: 4")
  const prev = /(?:^|[^\p{L}])(\p{L}+)(?:\s+(\p{L}+))?\s*(?:[(#:]\s*)?$/u.exec(before);
  if (prev === null) return false;
  const [label, head] = prev[2] === undefined ? [prev[1]!, ""] : [prev[2], prev[1]!];
  const twoWords = prev[2] === undefined ? "" : `${prev[1]!} ${prev[2]}`;
  if (twoWords !== "" && wordIsNoun(twoWords, LABEL_NOUNS_BEFORE_NUMBER, unaccented)) return true;
  return wordIsNoun(label, LABEL_NOUNS_BEFORE_NUMBER, unaccented) && !PURPOSE_HEAD_WORDS.has(head.toLowerCase());
}

/** Token số TRẦN. Thứ tự các luật là một phần của hợp đồng. */
function classifyBareToken(m: RegExpMatchArray, numPart: string, before: string, after: string, afterNum: string, ctx: TokenContext): TokenVerdict {
  // Mơ hồ về BẬC thắng mọi phân loại khác của cùng token — và bỏ CẢ CÂU.
  const mag = AMBIGUOUS_MAGNITUDE_AFTER_NUMBER_RE.exec(after);
  if (mag !== null) return ambiguousToken(mag[1] ?? null);
  // Số của KỲ ("tháng 7", "T7") · NĂM · một nửa hình NGÀY ("15/9") — không phải tiền; xét TRƯỚC các vị-từ "số sát số" (rà vòng 2:
  // "thu quỹ tháng 9 300k" · "ngày 15/9 300k" · "quỹ 2026 500k" từng bị hỏi oan). Riêng "1-2tr" trông như nửa ngày "1-2" ⇒ khoảng.
  // Số của KỲ chỉ là SỐ NGUYÊN ("tháng 7"); "mỗi tháng 1,5, cả năm 18 triệu" là số tiền thập phân (rà vòng 4).
  if (PERIOD_WORD_BEFORE_NUMBER_RE.test(before) && !/[.,]/u.test(numPart)) return SKIP;
  if (YEAR_LIKE_RE.test(numPart) || FULL_DATE_RE.test(numPart)) return SKIP;
  if (isDateHalf(numPart, before, after) && !RANGE_TO_UNIT_NUMBER_RE.test(afterNum)) return SKIP;
  if (bareNumberJoinsNextAmount(numPart, afterNum, before, ctx)) return ambiguousToken(numPart);
  // 🔴 US-AIV2-0112 H04 (@UC0162): chữ số DÍNH LIỀN đơn vị ("1tr2 người") là đuôi ghép HAY số người? ⇒ hỏi — xét TRƯỚC luật danh từ đếm
  // (trước: "2 người" bị bỏ ⇒ 1.000.000 tự tin). Có dấu cách ("1tr 2 người") thì "2" là số người như cũ.
  if (COMPOUND_HEAD_GLUED_BEFORE_NUMBER_RE.test(before)) return ambiguousToken(numPart);
  // Đơn vị kỳ/việc ("3 tháng", "1,5 tháng", "1 triệu 3 tháng" — S18 giữ 1.000.000) là SỐ LƯỢNG khi số không phải một số tiền trần ≥4 chữ số nguyên
  // (AI-INTERP N3: "200000 tháng này" là tiền); danh từ NGƯỜI thì luôn là số lượng.
  const wholeBareAmount = !isBareDecimal(numPart) && numPart.replace(/\D/gu, "").length >= BARE_MIN_DIGITS;
  // Đuôi số ghép ("1 triệu 2", "2tr,5") ⇒ bậc không xác định ⇒ bỏ CẢ CÂU, hỏi lại — xét TRƯỚC luật bỏ số nhỏ. Chỉ danh từ đếm CHẮC CHẮN cứu được.
  if (COMPOUND_HEAD_BEFORE_NUMBER_RE.test(before) || COMPOUND_HEAD_SEP_BEFORE_NUMBER_RE.test(before) || UNIT_NUMBER_LINKED_BEFORE_RE.test(before)) {
    // 🔴 vá review HIGH H2 (@UC0162): ở vị trí ĐUÔI GHÉP chỉ tập của develop (luật H04 "1 triệu 2 người") cứu được số — "người"/"thành viên" CÓ dấu +
    // đơn vị kỳ. Bản N3 nới cho mọi danh từ người ⇒ «chi 1tr 2 bạn» · «nộp 1 triệu 2 suất» ra 1.000.000 tự tin (develop hỏi; đổi luật cần QĐ PO).
    const certainCount = CERTAIN_HEAD_COUNT_NOUN_AFTER_RE.test(after) || (!wholeBareAmount && PERIOD_COUNT_NOUN_AFTER_RE.test(after));
    return certainCount ? SKIP : ambiguousToken(numPart);
  }
  // Số tiền trần ≥4 chữ số: chỉ danh từ người CHẮC (có dấu, tập develop) bỏ được số (vá review C2). 🔴 vá re-review HIGH: số THẬP PHÂN trần ("1,5" ·
  // "2.5") cũng KHÔNG đi tập rộng — như develop, chỉ người/thành viên có dấu + đơn vị kỳ ("1,5 tháng"); còn lại là tín hiệu thập phân ⇒ HỎI. Đi tập rộng thì
  // «chuyển 1,5 bạn Lan, 2 triệu bạn Hùng» ra 2.000.000 tự tin. Số nguyên nhỏ: mọi danh từ người/kỳ (N1/N3).
  const decimal = isBareDecimal(numPart);
  const countWord = wholeBareAmount
    ? CERTAIN_HEAD_COUNT_NOUN_AFTER_RE.test(after)
    : decimal
      ? CERTAIN_HEAD_COUNT_NOUN_AFTER_RE.test(after) || PERIOD_COUNT_NOUN_AFTER_RE.test(after)
      : COUNT_NOUN_AFTER_NUMBER_RE.test(after);
  if (countWord) return SKIP;
  if (decimal) return isNonMoneyDecimalContext(before, after, numPart) ? SKIP : { kind: "decimal-signal" };
  // Số trần nhỏ (<4 chữ số) — không phải tiền, không thành ứng viên. AI-INTERP N1: nhưng KHÔNG vô hình — không giải thích được vai ⇒ `unclear`.
  if (numPart.replace(/\D/gu, "").length < BARE_MIN_DIGITS) return isExplainedSmallNumber(before, after, ctx) ? SKIP : { kind: "unclear", token: numPart };
  const parsed = parseVndAmountMinor(m[0].trim());
  // S29 (rà vòng 2): số trần CÓ dấu phân cách, ≥4 chữ số, mà không đọc được ("100,000" · "1,500") ⇒ mơ hồ, không vô hình.
  if (parsed === null) return /[.,]/u.test(numPart) ? ambiguousToken(numPart) : SKIP;
  return parsed > 0n ? { kind: "value", value: parsed } : SKIP;
}

/** Phân loại MỘT token của `MONEY_TOKEN_RE` trong câu (đã viết lại số chữ). */
function classifyMoneyToken(message: string, m: RegExpMatchArray, sentenceUnaccented: boolean): TokenVerdict {
  const numPart = m[1] ?? "";
  const unit = m[2] ?? "";
  if (numPart === "") return SKIP;
  const index = m.index ?? 0;
  const before = windowBefore(message, index);
  const after = windowAfter(message, index + m[0].length);
  if (unit !== "") return classifyUnitToken(m, numPart, unit, before, after);
  // `after` đã mất khoảng trắng mà `\s*` của token nuốt — các vị-từ "số đứng sát số" đo trên phần NGAY SAU CHỮ SỐ.
  const ctx: TokenContext = { atListStart: atListStartOf(message, index), sentenceUnaccented };
  return classifyBareToken(m, numPart, before, after, windowAfter(message, index + numPart.length), ctx);
}

/**
 * Rút số tiền trong câu. Mơ hồ ⇒ `ambiguous` (kèm lý do/ứng viên) — KHÔNG BAO GIỜ chọn con số mình
 * thích hơn (CLAUDE.md §4, zero-tolerance).
 */
export function extractMoneyCandidates(input: string): MoneyParseResult {
  // 🔴 US-AIV2-0110 S18 (@UC0262): số viết bằng CHỮ ("hai trăm nghìn") ⇒ chữ số TRƯỚC mọi luật dưới — nhờ vậy mọi luật an toàn
  // ("2 trăm" · đuôi ghép · rưỡi · năm · danh từ đếm) áp nguyên cho dạng chữ, không viết lại luật nào. Cụm chữ có từ bậc mà không
  // đọc được thành MỘT cách ("sáu bảy trăm nghìn") ⇒ bỏ CẢ CÂU, hỏi lại — không đọc nửa cụm.
  // 🔴 US-AIV2-0112 H04 (review #1038 HIGH-1 · @UC0162): SỐ ĐIỆN THOẠI không phải tiền, ở MỌI dạng ("0912345678" · "0912 345 678" · "0912-345-678" ·
  // "+84912345678") — thay bằng `[SĐT]` TRƯỚC khi tách số (định nghĩa SĐT duy nhất: `maskPhoneNumbers` của bộ cạo PII). Trước: 912 đ / 84.912.345.678 đ
  // tự tin; "chuyển 500k cho 0912 345 678" ra nút 912 đ. Hệ quả chấp nhận: số tiền viết liền mở đầu "84"/"0" dài 10–12 chữ số bị coi là SĐT ⇒ hỏi.
  const words = rewriteViNumberWords(maskPhoneNumbers(collapseWhitespace(input.replace(FORMAT_CHAR_RE, ""))));
  if (words.ambiguousRun !== null) {
    return { kind: "ambiguous", reason: MONEY_PARSE_REASON.AMBIGUOUS_MAGNITUDE_WORD, token: null };
  }
  const message = words.text;
  // «rưỡi» sau một số tiền ⇒ bỏ CẢ CÂU (luật ở vi-normalizer, đây chỉ gọi). Đặt ĐẦU hàm: bỏ mỗi ứng
  // viên thì "chi 500k, còn 1 triệu rưỡi nữa" ra 500.000 — câu mơ hồ bỗng được điền một con số.
  if (hasAmbiguousHalfAmount(message)) {
    return { kind: "ambiguous", reason: MONEY_PARSE_REASON.AMBIGUOUS_MAGNITUDE_WORD, token: null };
  }
  // S29 (rà đối kháng vòng 2): "từ nửa đến 1 triệu" · "nửa hay 1 triệu" — khoảng mở bằng "nửa" ⇒ hỏi (từng ra 1.000.000).
  const halfRange = HALF_RANGE_RE.test(message) && !UNIT_NUMBER_BEFORE_HALF_RE.test(message);
  if (halfRange || WORD_NUMBER_RANGE_RE.test(message) || HIDDEN_NUMBER_RE.test(message)) {
    return { kind: "ambiguous", reason: MONEY_PARSE_REASON.AMBIGUOUS_MAGNITUDE_WORD, token: null };
  }
  const mentions: MoneyMention[] = [];
  const unclear: string[] = [];
  // Số thập phân dính một chữ KHÔNG phải đơn vị tiền ("Lan 1.5M, Hùng 500k" — rà vòng 3) không khớp token nào ⇒ vẫn là tín hiệu.
  let decimalSignal = [...message.matchAll(/(?<![\d.,])\d+[.,]\d{1,2}(?=\p{L})/gu)].some((g) => {
    const rest = message.slice((g.index ?? 0) + g[0].length);
    return !UNIT_AT_START_RE.test(rest) && !DECIMAL_MEASURE_AFTER_RE.test(rest);
  });
  const sentenceUnaccented = normalizeViText(message) === message.toLowerCase();
  for (const m of message.matchAll(MONEY_TOKEN_RE)) {
    const verdict = classifyMoneyToken(message, m, sentenceUnaccented);
    if (verdict.kind === "ambiguous") return verdict.result;
    if (verdict.kind === "decimal-signal") decimalSignal = true;
    if (verdict.kind === "unclear") unclear.push(verdict.token);
    if (verdict.kind === "value") mentions.push({ value: verdict.value, start: m.index ?? 0, end: (m.index ?? 0) + m[0].length });
  }
  for (const g of message.matchAll(SLANG_GLUED_AMOUNT_RE)) unclear.push(g[0]);
  const values = mentions.map((x) => x.value);
  // S29 (rà đối kháng vòng 2): số thập phân TRẦN ("1,5 chứ không phải 2 triệu", "1.5 hay là 2 triệu") không được VÔ HÌNH khi câu còn
  // số tiền khác — không chọn số còn lại.
  if (decimalSignal && values.length > 0) return { kind: "ambiguous", reason: MONEY_PARSE_REASON.AMBIGUOUS_MAGNITUDE_WORD, token: null };
  // Rà vòng 2 S18: cụm số chữ KHÔNG đọc được ("vài trăm nghìn", "nửa nghìn") không được VÔ HÌNH — còn số khác ⇒ hỏi (không chọn
  // số còn lại); không số nào ⇒ không đọc được (S17 hỏi lại có gợi ý).
  if (words.unreadRun !== null) {
    return values.length === 0 ? { kind: "none" } : { kind: "ambiguous", reason: MONEY_PARSE_REASON.AMBIGUOUS_MAGNITUDE_WORD, token: null };
  }
  if (values.length === 0) return { kind: "none" };
  return mentionsVerdict(message, mentions, unclear, sentenceUnaccented);
}

/** Một đề cập TIỀN của câu (đã viết lại số chữ): giá trị + đoạn `[start, end)` của token. */
interface MoneyMention {
  readonly value: bigint;
  readonly start: number;
  readonly end: number;
}

/**
 * Số dính chữ TIẾNG LÓNG tiền ("2m" = 2 triệu · "3lít" = 300k · "2xị") — `MONEY_TOKEN_RE` không mở token dính chữ không phải đơn vị ⇒ trước N1 vô hình.
 * Có khoảng trắng ("3 lít") thì đã là token trần ⇒ `isExplainedSmallNumber` không tha (tiếng lóng cố ý vắng ở tập loại từ).
 */
const SLANG_GLUED_AMOUNT_RE = /(?<![\p{L}\d.,])\d+(?:m|lít|lit|xị|xi|cành)(?![\p{L}\d])/giu;

/**
 * Chữ được phép đứng GIỮA hai đề cập TIỀN cùng giá trị mà vẫn là MỘT khoản (nhắc lại để xác nhận: "200k nhé, đúng 200k" · "200k (200.000đ)" · "200k tức
 * là 200 nghìn"). Tập ĐÓNG hư từ/xác nhận; chữ nội dung nào khác (tên, mục, thời gian: "Lan … Hùng" · "ăn sáng và … gửi xe" · "hôm qua … hôm nay") ⇒ HAI
 * khoản. Thiếu một từ ⇒ hỏi thừa (an toàn). ĐỪNG thêm "chi"/"thu"/"đóng": "chi 200k chi 200k" là hai lần chi hay gõ lặp — không biết.
 *
 * 🔴 vá review HIGH H1 (@UC0209) — bản trước so SAU khi bỏ dấu ⇒ tên/đại từ/xưng hô trùng mặt chữ một hư từ (Minh·mình · Nhi·nhỉ · Hà·hả · Ánh·anh ·
 * Chính · Nghĩa · chú·chứ · má·mà · nội·nói) thành "nhắc lại" ⇒ «Lan 200k, Minh 200k» · «cô Lan 200k, chú 200k» · «anh 200k em 200k» gộp thành MỘT khoản
 * 200.000. Nay: so trên chữ CÒN DẤU (NFC, chữ thường) với tập viết ĐÚNG chính tả; chữ viết HOA đầu là tên riêng ⇒ luôn là chữ nội dung; đại từ
 * "anh/em/mình/a" không còn được tha; "chính"/"nghĩa" (cũng là tên) chỉ tha trong cụm "chính xác"/"nghĩa là". Câu gõ KHÔNG dấu: dạng không dấu của hầu hết
 * hư từ trùng một tên/danh từ ("dung" = Dung · "nha" = nhà · "nho" = nho · "noi" = nội) ⇒ chỉ tha vài chữ không thể là tên (ok · uh · um · nhe) và cụm hai chữ.
 */
const RESTATEMENT_WORDS: ReadonlySet<string> = new Set([
  "nhé", "nha", "nhá", "à", "ạ", "hả", "nhỉ", "đúng", "đó", "đấy", "là", "tức", "xác", "ok", "oke", "okay", "vâng", "dạ", "ừ", "uh", "um", "ừm", "rõ",
  "nhớ", "luôn", "thôi", "mà", "chứ", "thật",
]);
/** Câu gõ KHÔNG dấu toàn bộ: chỉ chữ không thể là tên/danh từ (thay cho tập trên, không cộng thêm). */
const RESTATEMENT_WORDS_UNACCENTED: ReadonlySet<string> = new Set(["nhe", "ok", "oke", "okay", "uh", "um"]);
/**
 * Cụm hai chữ nhắc lại — câu có dấu / câu không dấu. 🔴 vá re-review MEDIUM: "lại" · "nhắc" · "nói" chỉ tha trong CỤM ("nhắc lại" · "nói lại"); đứng ĐƠN thì
 * «chi 200k, lại 200k» là chi THÊM một lần (hai khoản) — bản trước tha chữ đơn ⇒ gộp thành một 200.000.
 */
const RESTATEMENT_PAIRS: ReadonlySet<string> = new Set(["chính xác", "nghĩa là", "tức là", "nhắc lại", "nói lại"]);
const RESTATEMENT_PAIRS_UNACCENTED: ReadonlySet<string> = new Set(["chinh xac", "nghia la", "tuc la", "nhac lai", "noi lai"]);

/**
 * Đoạn `between` (giữa hai đề cập cùng giá trị) chỉ gồm chữ nhắc lại/xác nhận. Câu không dấu chỉ dùng tập không dấu — kể cả với chữ vốn viết không dấu
 * ("nha" trong câu có dấu là trợ từ; trong câu không dấu có thể là "nhà").
 */
function onlyRestatementWords(between: string, sentenceUnaccented: boolean): boolean {
  const singles = sentenceUnaccented ? RESTATEMENT_WORDS_UNACCENTED : RESTATEMENT_WORDS;
  const pairs = sentenceUnaccented ? RESTATEMENT_PAIRS_UNACCENTED : RESTATEMENT_PAIRS;
  const words = between.normalize("NFC").match(/[\p{L}\p{N}]+/gu) ?? [];
  for (let i = 0; i < words.length; i++) {
    const raw = words[i]!;
    if (/^\p{Lu}/u.test(raw)) return false; // tên riêng
    const w = raw.toLowerCase();
    if (pairs.has(`${w} ${(words[i + 1] ?? "").toLowerCase()}`)) i++;
    else if (!singles.has(w)) return false;
  }
  return true;
}

/** Câu đã có ≥1 số tiền: đề cập chưa rõ vai ⇒ hỏi; ≥2 giá trị khác nhau ⇒ ứng viên; cùng giá trị ở hai khoản ⇒ hỏi; còn lại ⇒ một số. */
function mentionsVerdict(message: string, mentions: readonly MoneyMention[], unclear: readonly string[], sentenceUnaccented: boolean): MoneyParseResult {
  if (unclear.length > 0) return { kind: "ambiguous", reason: MONEY_PARSE_REASON.AMBIGUOUS_MENTIONS, token: unclear[0]! };
  const distinct: bigint[] = [];
  for (const m of mentions) if (!distinct.some((d) => d === m.value)) distinct.push(m.value);
  if (distinct.length !== 1) {
    return {
      kind: "ambiguous",
      reason: MONEY_PARSE_REASON.AMBIGUOUS_MULTI_VALUE,
      candidates: Object.freeze([...distinct]),
    };
  }
  for (let i = 1; i < mentions.length; i++) {
    if (!onlyRestatementWords(message.slice(mentions[i - 1]!.end, mentions[i]!.start), sentenceUnaccented)) {
      return { kind: "ambiguous", reason: MONEY_PARSE_REASON.AMBIGUOUS_MENTIONS, token: null };
    }
  }
  return { kind: "resolved", amountMinor: distinct[0]! };
}

/** Một đoạn `[start, end)` của câu GỐC: chữ số + từ bậc chưa rõ ("2 trăm", "3 chục"). */
export interface AmbiguousMagnitudeRun {
  readonly start: number;
  readonly end: number;
  /** Phần chữ số của đoạn, nguyên văn ("2", "1,5"). */
  readonly digits: string;
}

/**
 * 🔴 AI-COL-OPEN-PHRASING (@UC0162) — VỊ TRÍ các cụm "N trăm"/"N chục" mà bộ đọc tiền coi là MƠ HỒ VỀ BẬC, trên chính câu đưa vào
 * (không viết lại số chữ: chỉ chữ SỐ — đoạn trả về luôn là chuỗi con của `input`). CÙNG token (`MONEY_TOKEN_RE`) và CÙNG vị-từ
 * (`AMBIGUOUS_MAGNITUDE_AFTER_NUMBER_RE`, nhánh đầu của `classifyBareToken`) với `extractMoneyCandidates` — một luật, hai người đọc.
 * Dùng để CHE cụm đó khỏi Người phiên dịch (`interpreter/llm-amount-mask.ts`); quyết định "hỏi lại" vẫn là của bộ đọc tiền.
 */
export function ambiguousMagnitudeRuns(input: string): readonly AmbiguousMagnitudeRun[] {
  const runs: AmbiguousMagnitudeRun[] = [];
  for (const m of input.matchAll(MONEY_TOKEN_RE)) {
    const numPart = m[1] ?? "";
    if (numPart === "" || (m[2] ?? "") !== "") continue;
    const index = m.index ?? 0;
    const mag = AMBIGUOUS_MAGNITUDE_AFTER_NUMBER_RE.exec(input.slice(index + m[0].length));
    if (mag === null) continue;
    runs.push({ start: index, end: index + m[0].length + mag[0].length, digits: numPart });
  }
  return runs;
}

/** Dấu phân nghìn của tiền VND trên câu/thẻ trợ lý — MỘT chỗ, trùng `_groupThousands` của mobile (`money.dart`). */
const VND_THOUSANDS_SEPARATOR = ".";

/**
 * Định dạng tiền để HIỂN THỊ — nguồn DUY NHẤT cho mọi số tiền trong câu/thẻ/nhãn của trợ lý (các `money()` ở bindings chỉ là
 * vỏ gọi hàm này): nhóm nghìn bằng dấu CHẤM + " đ", âm giữ dấu. Format ≠ giá trị — giá trị luôn là `bigint`, không qua `Number`.
 *
 * 🔴 QĐ PO 01/10/2026 phương án C (US-AIV2-0114 L02) — ĐẢO QĐ 08/09 B-31, CHỈ đổi dấu: `500.000 đ` (trước là `500,000 đ`).
 * B-31 vẫn đứng: server và app mobile (`formatMoneyMinor`) ra ĐÚNG cùng chuỗi — bảng chung
 * `packages/contracts/fixtures/money-vnd-display.json`, hai bên cùng đọc. Lý do đảo: người chép lại số trợ lý in ra thì bộ đọc
 * phải đọc đúng — dạng dấu phẩy bị chính bộ đọc coi là mơ hồ (S29, luật đó KHÔNG đổi). Admin/trang công khai/thông báo
 * (`500.000 ₫`, `Intl`) không thuộc hàm này.
 */
export function formatVndMinor(amountMinor: bigint): string {
  const neg = amountMinor < 0n;
  const digits = (neg ? -amountMinor : amountMinor).toString();
  let out = "";
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += VND_THOUSANDS_SEPARATOR;
    out += digits[i];
  }
  return `${neg ? "-" : ""}${out} đ`;
}
